import React, { useEffect, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import AssignmentIcon from "@material-ui/icons/Assignment";
import { Box, Hidden, makeStyles, Tooltip, Paper, Typography, Fade, ClickAwayListener } from "@material-ui/core";
import clsx from "clsx";
import { useHistory, useLocation } from "react-router-dom";
import { Fonts } from "shared/constants/AppEnums";
import { gql, useApolloClient } from "@apollo/client";
import moment from "moment";
import { getUserId } from "@crema/utility/AppHooks";
import { CremaTheme } from "types/AppContextPropsType";
import IntlMessages from "@crema/utility/IntlMessages";

const GET_TASK_COUNT_SUMMARY = gql`
  query GetTaskCountSummary($id: id!, $todayDate: String!, $userType: String!) {
    GetTaskCountSummary(id: $id, todayDate: $todayDate, userType: $userType) {
      tab_counts
      message
      status_code
    }
  }
`;

const useStyles = makeStyles((theme: CremaTheme) => ({
  notiBtn: {
    justifyContent: "flex-start",
    width: "100%",
    height: 56,
    fontSize: 16,
    borderRadius: 0,
    paddingLeft: "1rem",
    paddingRight: "1rem",
    color: theme.palette.grey[800],
    "&:hover, &:focus": {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
    },
    [theme.breakpoints.up("sm")]: {
      height: 70,
    },
    [theme.breakpoints.up("md")]: {
      justifyContent: "center",
      width: "auto",
      borderLeft: "solid 1px",
      borderColor: theme.palette.grey[200],
      color: theme.palette.grey[400],
      "&:hover, &:focus": {
        color: theme.palette.text.primary,
        backgroundColor: "rgba(0, 0, 0, 0.08)",
      },
    },
    [theme.breakpoints.up("lg")]: {
      paddingLeft: "1.5rem",
      paddingRight: "1.5rem",
    },
    [theme.breakpoints.up("xl")]: {
      paddingLeft: "2.5rem",
      paddingRight: "2.5rem",
    },
  },
  badgeStyle: {
    marginRight: 8,
  },
  taskIcon: {
    fontSize: 22,
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("xl")]: {
      fontSize: 30,
    },
  },
  popupContainer: {
    position: "relative",
    display: "inline-block",
  },
  popup: {
    position: "absolute",
    top: "60%",
    right: "calc(100% + 15px)",
    transform: "translateY(-50%)",
    zIndex: 9999,
    minWidth: 180,
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    overflow: "visible", // Needed for arrow
    "&::before": {
      content: '""',
      position: "absolute",
      right: -8,
      top: "calc(50% - 8px)",
      width: 0,
      height: 0,
      borderTop: "8px solid transparent",
      borderBottom: "8px solid transparent",
      borderLeft: `8px solid ${theme.palette.primary.main}`,
    },
  },
  popupContent: {
    padding: theme.spacing(1.5, 2),
  },
  popupTitle: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#ffffff",
    textAlign: "left",
    opacity: 0.9,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  countRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateX(-4px)",
    },
  },
  countLabel: {
    fontSize: 13,
    color: "#ffffff",
    opacity: 0.8,
    fontWeight: 400,
  },
  countValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: theme.spacing(0.2, 0.8),
    borderRadius: 4,
    minWidth: 24,
    textAlign: "center",
  },
}));

const HeaderTask: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const client = useApolloClient();

  const [showPopup, setShowPopup] = useState(false);
  const [counts, setCounts] = useState({
    assigned_incomplete: 0,
    created_incomplete: 0,
  });
  const currentUserId = getUserId();

  useEffect(() => {
    if (!currentUserId) return;

    const fetchCounts = () => {
      const todayDate = moment().format("DD/MM/YYYY");

      client
        .query({
          query: GET_TASK_COUNT_SUMMARY,
          variables: {
            id: currentUserId,
            todayDate: todayDate,
            userType: "practitioner",
          },
          fetchPolicy: "network-only",
        })
        .then((result) => {
          if (result?.data?.GetTaskCountSummary?.status_code === 200) {
            const parsedCounts = JSON.parse(result.data.GetTaskCountSummary.tab_counts);
            setCounts({
              assigned_incomplete: parsedCounts.assigned_incomplete || 0,
              created_incomplete: parsedCounts.created_incomplete || 0,
            });
          }
        })
        .catch((error) => console.error("Error fetching task counts:", error));
    };

    fetchCounts();

    // Refresh counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [client, currentUserId]);

  const totalCount = counts.assigned_incomplete;

  const handleRedirectTask = () => {
    history.push("/task-management?tab=assigned&filter=incomplete");
  };

  const handleMouseEnter = () => {
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    setShowPopup(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setShowPopup(false)}>
      <Box className={classes.popupContainer}>
        <Tooltip title={<IntlMessages id="dashboard.task" />} disableHoverListener={showPopup}>
          <IconButton
            className={clsx(classes.notiBtn)}
            color="inherit"
            onClick={handleRedirectTask}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Badge
              className={classes.badgeStyle}
              badgeContent={totalCount}
              color="secondary"
              max={999}
            >
              <AssignmentIcon className={classes.taskIcon} />
            </Badge>
            <Hidden mdUp>
              <Box
                ml={4}
                fontSize={16}
                fontFamily='Poppins'
                fontWeight={Fonts.REGULAR}
                component='span'>
                <IntlMessages id='dashboard.task' />
              </Box>
            </Hidden>
          </IconButton>
        </Tooltip>

        {/* <Fade in={showPopup} timeout={300}>
          <Paper
            className={classes.popup}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Box className={classes.popupContent}>
              <Box className={classes.countRow}>
                <Typography className={classes.countLabel}>My Tasks</Typography>
                <Typography className={classes.countValue}>{counts.assigned_incomplete}</Typography>
              </Box>

              <Box className={classes.countRow}>
                <Typography className={classes.countLabel}>Created by Me</Typography>
                <Typography className={classes.countValue}>{counts.created_incomplete}</Typography>
              </Box>
            </Box>
          </Paper>
        </Fade> */}
      </Box>
    </ClickAwayListener>
  );
};

export default HeaderTask;
